import React, { useCallback, useEffect, useState } from 'react'
import ReactSelect from 'react-select'
import { useField } from 'formik'

import { FieldProps, Option } from '../../types'

import { FieldError } from '@components/form'

import { styles as stylesV1 } from './styles'
import { styles as stylesV2 } from './styles-v2'
interface SelectProps<T> extends FieldProps {
  id?: string
  placeholder?: string
  options: T[]
  emptyMessage?: string
  clearable?: boolean
  searchable?: boolean
  disabled?: boolean
  loading?: boolean
  loadingMessage?: string
  labelExtractor?: (option: T) => string
  onChange?: (option: T) => void
  tabIndex?: number
  itemSelected?: any
  styles?: 'v1' | 'v2'
}

export function Select<T>({
  id,
  name,
  label,
  container,
  error,
  onChange,
  disabled,
  loading,
  placeholder = '',
  clearable = true,
  searchable = true,
  loadingMessage = 'Carregando',
  emptyMessage = 'Nenhuma opção',
  labelExtractor = option => `${option}`,
  options,
  tabIndex,
  itemSelected,
  styles = 'v1'
}: SelectProps<T>) {
  const [, meta, helpers] = useField<T>(name)
  const [value, setValue] = useState<Option<T> | undefined>()
  let componentMounted = true

  const buildOption = useCallback(
    (option: T): Option<T> | undefined =>
      option
        ? {
            label: labelExtractor(option),
            value: option
          }
        : undefined,
    [labelExtractor]
  )

  useEffect(() => {
    if (componentMounted) {
      if (itemSelected) {
        setValue(itemSelected.value)
      } else {
        setValue(null)
      }
    }

    return () => {
      componentMounted = false
    }
  }, [itemSelected])

  return (
    <div {...container}>
      {label}
      <ReactSelect
        inputId={id}
        isClearable={clearable}
        isSearchable={searchable}
        placeholder={placeholder}
        noOptionsMessage={() => emptyMessage}
        loadingMessage={() => loadingMessage}
        onChange={optionSelected => {
          onChange && onChange(optionSelected?.value)
          helpers.setTouched(!optionSelected)

          helpers.setValue(optionSelected?.value)
          setValue(optionSelected)
        }}
        name={name}
        isLoading={loading}
        isDisabled={disabled}
        value={value}
        options={options.map(buildOption)}
        styles={
          styles === 'v1'
            ? stylesV1<T>(meta.touched, meta.error)
            : stylesV2<T>(meta.touched, meta.error)
        }
        tabIndex={tabIndex}
      />
      <FieldError touched={meta.touched} error={meta.error} {...error} />
    </div>
  )
}
